import { useState } from "react";
import { useRequestUploadUrlMutation, useRegisterAssetMutation } from "../redux/api";
import type { UploadEntityType } from "../redux/api";

const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB — mirrors the backend's own limit (see services/src/common/aws/s3/key.ts)

export interface UploadedImage {
  key: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}

// Shared upload flow for Product images / Brand logo / Organization logo:
// validate client-side first (avoids a round trip for an obviously-bad
// file), ask the backend for a presigned S3 PUT url, then PUT the file
// directly to S3 — the file never passes through our own server. The
// bucket is private, so this returns the raw `key`, not a URL — display
// URLs come back already resolved on whatever query/mutation reads the
// owning record (Product/Brand/Organization each have a computed field
// resolver for this — see services/src/modules/upload/graphql.ts and the
// per-entity resolvers).
export const useImageUpload = (entityType: UploadEntityType, entityId: string) => {
  const [requestUploadUrl] = useRequestUploadUrlMutation();
  const [registerAsset] = useRegisterAssetMutation();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File): Promise<UploadedImage | null> => {
    setError(null);

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setError("Only image files (jpeg, png, webp, gif) are allowed.");
      return null;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File is too large — maximum size is ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB.`);
      return null;
    }

    setUploading(true);
    try {
      const { uploadUrl, key } = await requestUploadUrl({
        entityType,
        entityId,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
      }).unwrap();

      // Content-Type MUST match what was signed, or S3 rejects the PUT.
      const putResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error(`Upload to storage failed (${putResponse.status}).`);
      }

      // Best-effort — the upload itself already succeeded, so a failure here
      // (e.g. a transient network blip) shouldn't fail the whole operation;
      // it just means this file won't show up in the Gallery.
      try {
        await registerAsset({
          key,
          fileName: file.name,
          mimeType: file.type,
          fileSize: file.size,
        }).unwrap();
      } catch {
        // Swallow — see comment above.
      }

      return { key, fileName: file.name, mimeType: file.type, fileSize: file.size };
    } catch (err) {
      setError((err as { message?: string })?.message ?? "Failed to upload image.");
      return null;
    } finally {
      setUploading(false);
    }
  };

  return { upload, uploading, error };
};
